// The Desert Book theme covers the front door and the stories, nothing else.
// Keep this list in sync with the inline script in index.html and with
// DESERT_PATHS in scripts/prerender.mjs (plain ESM, can't import TS).
export const DESERT_PATHS = ["/", "/in-motion/recovery", "/in-motion/blocks", "/learn", "/contributors", "/implement", "/writing", "/trust", "/about", "/research", "/advocacy", "/governed-autonomy", "/governed-autonomy/enterprise", "/governed-autonomy/risks", "/governed-autonomy/architecture", "/governed-autonomy/roadmap", "/governed-autonomy/ciso", "/governed-autonomy/methods", "/governed-autonomy/skeptical", "/governed-autonomy/defense-map", "/governed-autonomy/home-grown", "/governed-autonomy/operating-model", "/governed-autonomy/semantic-join", "/governed-autonomy/knowledge-graph", "/governed-autonomy/mission-join", "/governed-autonomy/work-types", "/governed-autonomy/workforce", "/governed-autonomy/playbooks", "/governed-autonomy/tests", "/governed-autonomy/writing-rules", "/governed-autonomy/glossary", "/advocacy/sfps", "/advocacy/sfps/parent-1pager", "/advocacy/sfps/file-an-ipra", "/advocacy/sfps/thought-partner", "/advocacy/sfps/clocks", "/advocacy/sfps/scorecard", "/advocacy/sfps/proposal", "/advocacy/sfps/sources", "/advocacy/sfps/amira-policy-diff", "/advocacy/sfps/lesc-hearing", "/advocacy/sfps/the-record", "/research/ambient-breach", "/research/ambient-breach/sound", "/research/cartography", "/specs", "/community", "/start", "/canon", "/find-your-path", "/language", "/colophon"];

export function isDesertPath(p: string): boolean {
  const path = p.length > 1 ? p.replace(/\/+$/, "") : p;
  return DESERT_PATHS.includes(path || "/");
}

// Z9 in the story framework: the footer's last line, in the story's voice, so the page ends on the story and not on
// the site's tagline. One line per desert route; the data files keep the same text in `back.lastLine` for the map.
const LAST_LINES: Record<string, string> = {
  "/": "Governance at Rest and Governance in Motion.",
  "/in-motion/recovery": "Governance at Rest and Governance in Motion.",
  "/in-motion/blocks": "Every block, one card each.",
  "/learn": "Governance at Rest and Governance in Motion.",
  "/contributors": "Governance at Rest and Governance in Motion.",
  "/implement": "Governance at Rest and Governance in Motion.",
  "/writing": "Governance at Rest and Governance in Motion.",
  "/trust": "Governance at Rest and Governance in Motion.",
  "/about": "Governance at Rest and Governance in Motion.",
  "/research": "Governance at Rest and Governance in Motion.",
  "/advocacy": "Governance at Rest and Governance in Motion.",
  "/governed-autonomy": "Governance at Rest and Governance in Motion.",
  "/governed-autonomy/risks": "Governance at Rest and Governance in Motion.",
  "/governed-autonomy/architecture": "Governance at Rest and Governance in Motion.",
  "/governed-autonomy/roadmap": "Governance at Rest and Governance in Motion.",
  "/governed-autonomy/ciso": "Governance at Rest and Governance in Motion.",
  "/governed-autonomy/methods": "Governance at Rest and Governance in Motion.",
  "/governed-autonomy/skeptical": "Governance at Rest and Governance in Motion.",
  "/governed-autonomy/defense-map": "Governance at Rest and Governance in Motion.",
  "/governed-autonomy/home-grown": "Governance at Rest and Governance in Motion.",
  "/governed-autonomy/enterprise": "Governance at Rest and Governance in Motion.",
  "/governed-autonomy/operating-model": "Governance at Rest and Governance in Motion.",
  "/governed-autonomy/semantic-join": "Governance at Rest and Governance in Motion.",
  "/governed-autonomy/knowledge-graph": "Governance at Rest and Governance in Motion.",
  "/governed-autonomy/mission-join": "Governance at Rest and Governance in Motion.",
  "/governed-autonomy/work-types": "Governance at Rest and Governance in Motion.",
  "/governed-autonomy/workforce": "Governance at Rest and Governance in Motion.",
  "/governed-autonomy/playbooks": "Governance at Rest and Governance in Motion.",
  "/governed-autonomy/tests": "Governance at Rest and Governance in Motion.",
  "/governed-autonomy/writing-rules": "Governance at Rest and Governance in Motion.",
  "/governed-autonomy/glossary": "Governance at Rest and Governance in Motion.",
  "/advocacy/sfps": "Governance at Rest and Governance in Motion.",
  "/advocacy/sfps/parent-1pager": "Governance at Rest and Governance in Motion.",
  "/advocacy/sfps/file-an-ipra": "Governance at Rest and Governance in Motion.",
  "/advocacy/sfps/thought-partner": "Governance at Rest and Governance in Motion.",
  "/advocacy/sfps/clocks": "Governance at Rest and Governance in Motion.",
  "/advocacy/sfps/scorecard": "Governance at Rest and Governance in Motion.",
  "/advocacy/sfps/proposal": "Governance at Rest and Governance in Motion.",
  "/advocacy/sfps/sources": "Governance at Rest and Governance in Motion.",
  "/advocacy/sfps/amira-policy-diff": "Governance at Rest and Governance in Motion.",
  "/advocacy/sfps/lesc-hearing": "Governance at Rest and Governance in Motion.",
  "/advocacy/sfps/the-record": "Governance at Rest and Governance in Motion.",
  "/research/ambient-breach": "Governance at Rest and Governance in Motion.",
  "/research/ambient-breach/sound": "Governance at Rest and Governance in Motion.",
  "/research/cartography": "Governance at Rest and Governance in Motion.",
  "/specs": "Governance at Rest and Governance in Motion.",
  "/community": "Governance at Rest and Governance in Motion.",
  "/start": "Governance at Rest and Governance in Motion.",
  "/canon": "Governance at Rest and Governance in Motion.",
  "/find-your-path": "Governance at Rest and Governance in Motion.",
  "/language": "Governance at Rest and Governance in Motion.",
};

export function lastLineFor(p: string): string | undefined {
  const path = p.length > 1 ? p.replace(/\/+$/, "") : p;
  return LAST_LINES[path || "/"] ?? SITE_LAST_LINE;
}

// The site's own last line: SiteFooter ends every reference page on it, as the Desert Book pages end.
export const SITE_LAST_LINE = "Governance at Rest and Governance in Motion.";

// Routes that get the small footer: the front door, the stories, and /audit ("audit gets the smallest footer we have",
// Chris, 2026-10-27; /audit paints its own paper and is not a desert route). Every other Desert Book page gets the full
// footer with the site map.
const COMPACT_FOOTER_PATHS = ["/", "/in-motion/recovery", "/in-motion/blocks", "/audit", "/audit/culture"];

export function isCompactFooter(p: string): boolean {
  const path = p.length > 1 ? p.replace(/\/+$/, "") : p;
  return COMPACT_FOOTER_PATHS.includes(path || "/");
}

// The horizon: sky to sun. Both footers' top edge and the reference pages' Horizon bar, so both rooms read as one building.
export const HORIZON = "linear-gradient(90deg, #396ea4 0%, #d9a35f 100%)";
